import { type ReactNode, useContext } from 'react';
import { CartContext } from '@r/context/cart-context.ts';
import { useApiOrdersPost } from '@r/api/generated/client.ts';
import type { OrderOrderWriteModel } from '@r/api/generated/client.schemas.ts';
import { useStepper } from '@client/pages/cart/cart.tsx';
import { Card, CardContent, CardHeader, CardFooter, CardTitle } from '@ui/card.tsx';
import { Button } from '@ui/button.tsx';
import defaultAsset from '@r/assets/images/fiddy/default-product-image.png';
import { useUser } from '@r/api/auth.tsx';
import pointsTranslation from '@r/translations/points-translation.ts';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { mapCartItemToStoreCartLineItem } from '@client/utils/cart-utils.ts';
import { useRouteExists } from '@r/hooks/use-route-exists.ts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/table.tsx';
import { useBalance } from '@r/context/balance-context.ts';
import { FormattedHtml } from '@r/components/formatted-html.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { getOrvalErrorMessage, isOrvalError } from '@r/api/orval-error-helper.ts';
import { useCartHealthcheck } from '@client/hooks/use-cart-healthcheck.ts';
import { Alert, AlertTitle } from '@ui/alert.tsx';
import { TriangleAlert } from 'lucide-react';
import { DeleteButton } from '@client/components/cart/delete-button.tsx';
import { cn } from '@r/utils/utils.ts';

type Order = OrderOrderWriteModel;

const ItemLink = ({ children, to }: { children: ReactNode; to: string }) => (
  <Link to={to} className="hover:underline">
    {children}
  </Link>
);

const OrderConfirmation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useUser().data;
  const activeWorkspace = getActiveWorkspaceUniversal(user?.visibleWorkspaces);
  const company = activeWorkspace?.company;
  const { balance } = useBalance();
  const stepper = useStepper();
  const { cartItems, cartWithTotals, clearCart } = useContext(CartContext);
  const { healthcheckFailed, invalidProductIris, isCartValid, isCheckingCart, refreshHealthcheck } =
    useCartHealthcheck(cartItems);
  const items = cartItems.map((item) => mapCartItemToStoreCartLineItem(item));
  const firstInvalidItem = items.find((item) => invalidProductIris.has(item.product_iri));
  const personalInfoRouteExists = useRouteExists('/personal-informations');
  const postalAddressKeywords = [
    'addressline1',
    'adressline1',
    'address line 1',
    'zipcode',
    'zip code'
  ];
  const isPostalAddressViolationText = (value: string | undefined, compact = false) => {
    const normalized = (value ?? '').toLowerCase();
    const candidate = compact ? normalized.replace(/\s/g, '') : normalized;
    return postalAddressKeywords.some((keyword) =>
      compact ? candidate.includes(keyword.replace(/\s/g, '')) : candidate.includes(keyword)
    );
  };

  const orderMutation = useApiOrdersPost({
    mutation: {
      onSuccess: (response) => {
        if (Array.isArray(response) && response.length > 0) {
          clearCart();
          stepper.setMetadata('step-4', { ordersResponse: response });
          stepper.next();
        }
      },
      onError: (error) => {
        console.error('Error submitting order:', error);
        if (isOrvalError(error) && error.response?.status === 422) {
          const violations = (
            error.response?.data as { violations?: Array<{ property?: string; detail?: string }> }
          )?.violations;
          const hasPostalAddressViolation = Array.isArray(violations)
            ? violations.some((violation) => {
                return (
                  isPostalAddressViolationText(violation.property, true) ||
                  isPostalAddressViolationText(violation.detail)
                );
              })
            : false;

          const errorMessage = getOrvalErrorMessage(error).toLowerCase();
          const isPostalAddressViolationFromMessage = isPostalAddressViolationText(errorMessage);

          if (hasPostalAddressViolation || isPostalAddressViolationFromMessage) {
            toast.error(() => <div>{t('order.error.shipping.postal')}</div>, { duration: 10000 });
            return;
          }
        }
        toast.error(t('order.error.submit'));
      }
    }
  });

  const handleSendOrder = async () => {
    // Vérification du panier
    if (cartItems.length === 0) {
      toast.error(t('order.error.empty.cart'));
      return;
    }

    // Vérification du solde de points disponible
    const total = cartWithTotals.totals.total ?? 0;

    if (total > balance) {
      toast.error(t('order.error.balance.insufficient'));
      return;
    }

    // Vérification des informations de livraison
    if (!company || !company.addressLine1 || !company.zipcode || !company.country) {
      toast.error(
        () => (
          <div>
            {t('order.error.shipping.missing')}{' '}
            {personalInfoRouteExists ? (
              <a href="/personal-informations" className="font-medium underline">
                {t('order.error.shipping.edit.link')}
              </a>
            ) : (
              <span>{t('order.error.shipping.help.text')}</span>
            )}
          </div>
        ),
        {
          duration: 10000
        }
      );

      return;
    }

    // Vérification des coordonnées du contact.
    if (!user?.firstname || !user?.lastname || !user?.email || !activeWorkspace?.phone) {
      toast.error(
        () => (
          <div>
            {t('order.error.contact.missing')}{' '}
            {personalInfoRouteExists ? (
              <a href="/personal-informations" className="font-medium underline">
                {t('order.error.contact.edit.link')}
              </a>
            ) : (
              <span>{t('order.error.contact.help.text')}</span>
            )}
          </div>
        ),
        {
          duration: 10000
        }
      );

      return;
    }

    try {
      const healthcheck = await refreshHealthcheck();

      if (!healthcheck.isValid) {
        const invalidProductIri = healthcheck.items.find(
          (item) => item.isValid === false || item.issue != null
        )?.product;
        const invalidItem = cartItems.find((item) => item.iri === invalidProductIri);
        toast.error(
          t('order.error.stock.insufficient', {
            productName: invalidItem?.product.name ?? ''
          })
        );
        return;
      }
    } catch (error) {
      console.error('Error checking cart health:', error);
      toast.error(t('order.error.stock.verification'));
      return;
    }

    const order: Order = {
      orderProducts: cartItems.map((item) => ({
        product: item.iri,
        quantity: item.quantity,
        ...(item.dynamicPriceAmount && { dynamicPriceAmount: item.dynamicPriceAmount })
      }))
    };

    orderMutation.mutate({ data: order });
  };

  const handleSubmit = () => {
    if (!orderMutation.isPending && !isCheckingCart) {
      void handleSendOrder();
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center">
        <h1 className="text-[2rem] leading-[2.75rem] font-semibold">{t('order.confirm.title')}</h1>
      </div>

      {!isCartValid && (
        <Alert variant="destructive">
          <div className="flex gap-2 items-center">
            <TriangleAlert className="size-4 shrink-0" />
            <AlertTitle className="mb-0 leading-normal">
              {t('order.error.stock.insufficient', {
                productName: firstInvalidItem?.product.name ?? ''
              })}
            </AlertTitle>
          </div>
        </Alert>
      )}

      {healthcheckFailed && (
        <Alert variant="destructive">
          <div className="flex gap-2 items-center">
            <TriangleAlert className="size-4 shrink-0" />
            <AlertTitle className="mb-0 leading-normal">
              {t('order.error.stock.verification')}
            </AlertTitle>
          </div>
        </Alert>
      )}

      <Card className="border-none">
        <CardContent className="pt-6">
          <Table>
            <TableHeader className="bg-transparent">
              <TableRow className="w-full text-sm sm:text-base md:text-lg hover:bg-transparent">
                <TableHead className="align-top">{t('order.products.list')}</TableHead>

                <TableHead className="align-top text-end">
                  {t('order.product.point.unit.price')}

                  <span className="hidden sm:inline">
                    {t('currency.in', { currency: t('point.name_other') })}
                  </span>

                  <p className="text-xs sm:hidden text-muted-foreground">
                    ({t('point.name_other')})
                  </p>
                </TableHead>

                <TableHead className="align-top text-end">
                  {t('order.amount')}

                  <span className="hidden sm:inline">
                    {t('currency.in', { currency: t('point.name_other') })}
                  </span>

                  <p className="text-xs sm:hidden text-muted-foreground">
                    ({t('point.name_other')})
                  </p>
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody className="border-gray-200 border-y">
              {items.map((item) => (
                <TableRow
                  key={`${item.product_iri}-${item.added_at}`}
                  className={cn(
                    'border-b border-gray-200 border-dashed hover:bg-transparent',
                    invalidProductIris.has(item.product_iri) &&
                      'bg-destructive/5 hover:bg-destructive/5'
                  )}
                >
                  <TableCell className="flex">
                    <div className="hidden overflow-hidden mr-4 w-20 rounded-2xl sm:block aspect-square">
                      <ItemLink to={item.product_iri}>
                        <img
                          className="object-cover object-center w-full h-full"
                          src={item.product.images?.[0]?.pathUri || defaultAsset}
                          alt={`${item.product.name} illustration`}
                          onError={(e) => {
                            e.currentTarget.src = defaultAsset;
                            e.currentTarget.onerror = null;
                          }}
                        />
                      </ItemLink>
                    </div>

                    <div className="flex-grow">
                      <ItemLink to={item.product_iri}>
                        <div className="text-sm font-medium sm:text-lg">
                          <FormattedHtml as="span" text={item.product.name} />
                          &nbsp;
                          {item.dynamic_price_amount &&
                            t('item.dynamic.price', {
                              amount: item.dynamic_price_amount,
                              currency: '€'
                            })}
                        </div>
                      </ItemLink>

                      <FormattedHtml
                        as="div"
                        text={item.product.category?.label}
                        className="text-xs text-primary/60 line-clamp-1"
                      />

                      <div className="text-sm text-muted-foreground">
                        {t('order.product.quantity')} {item.quantity}
                      </div>

                      {invalidProductIris.has(item.product_iri) && (
                        <div className="mt-2 space-y-2">
                          <p className="text-sm font-medium text-destructive">
                            {t('cart.error.stock.insufficient')}
                          </p>
                          <DeleteButton
                            cartItem={item}
                            variant="destructive"
                            size="sm"
                            className="rounded-full"
                            onDeleted={() => {
                              if (cartItems.length === 1) {
                                void navigate({ to: '/shops' });
                              }
                            }}
                          >
                            {t('cart.item.action.remove')}
                          </DeleteButton>
                        </div>
                      )}
                    </div>
                  </TableCell>

                  <TableCell>
                    {!item.dynamic_price_amount && (
                      <p className="text-end">{item.product.pointUnitPrice}</p>
                    )}
                  </TableCell>

                  <TableCell>
                    <p className="text-end">{item.total}</p>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>

        <CardFooter>
          <div className="flex justify-between w-full text-lg font-bold sm:text-lg">
            <div className="font-semibold">{t('order.total.amount')}</div>
            <div className="font-semibold">
              {pointsTranslation(t, cartWithTotals.totals.total, true)}
            </div>
          </div>
        </CardFooter>
      </Card>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Card className="border-none">
          <CardHeader>
            <CardTitle className="pb-4 mb-4 text-lg font-semibold border-b border-gray-200">
              {t('order.shipping.address')}
            </CardTitle>
          </CardHeader>

          <CardContent>
            <dl className="space-y-2 leading-relaxed">
              <div className="flex gap-2">
                <dt className="font-semibold">{t('order.company')}</dt>
                <dd>{company?.name}</dd>
              </div>

              <div className="flex gap-2">
                <dt className="font-semibold">{t('order.postal.address')}</dt>
                <dd>{company?.addressLine1}</dd>
              </div>

              {company?.addressLine2 && (
                <div className="flex gap-2">
                  <dt className="font-semibold">{t('order.address.complement')}</dt>
                  <dd>{company?.addressLine2}</dd>
                </div>
              )}

              <div className="flex gap-2">
                <dt className="font-semibold">{t('order.zipcode')}</dt>
                <dd>{company?.zipcode}</dd>
              </div>

              <div className="flex gap-2">
                <dt className="font-semibold">{t('order.country')}</dt>
                <dd>{company?.country?.name}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card className="border-none">
          <CardHeader>
            <CardTitle className="pb-4 mb-4 text-lg font-semibold border-b border-gray-200">
              {t('order.contact.details')}
            </CardTitle>
          </CardHeader>

          <CardContent>
            <dl className="space-y-2 leading-relaxed">
              <div className="flex gap-2">
                <dt className="font-semibold">{t('order.firstname')}</dt>
                <dd>{user?.firstname}</dd>
              </div>

              <div className="flex gap-2">
                <dt className="font-semibold">{t('order.lastname')}</dt>
                <dd>{user?.lastname}</dd>
              </div>

              <div className="flex gap-2">
                <dt className="font-semibold">{t('order.email')}</dt>
                <dd>{user?.email}</dd>
              </div>

              <div className="flex gap-2">
                <dt className="font-semibold">{t('order.phone')}</dt>
                <dd>{activeWorkspace?.phone}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-between mb-8">
        <Button variant="outline" rounded="full" onClick={() => stepper.prev()}>
          {t('common.back')}
        </Button>

        <Button onClick={handleSubmit} rounded="full" disabled={isCheckingCart || !isCartValid}>
          {orderMutation.isPending || isCheckingCart ? t('order.submitting') : t('order.submit')}
        </Button>
      </div>

      <div className="text-xs text-center text-foreground/50">
        <p className="mb-1">
          {t('order.confirmation.points.disclaimer')}
          {pointsTranslation(t, balance - (cartWithTotals.totals.total ?? 0), true)}.
        </p>

        <p>
          {t('order.confirmation.help.text')}
          <Link className="text-foreground/80 hover:underline" to={'/contact'}>
            {t('order.confirmation.contact.service')}
          </Link>
          .
        </p>
      </div>
    </div>
  );
};

export { OrderConfirmation };
